import {useApi, ApiAuthError} from '@composables/useApi';
import * as Sentry from '@sentry/browser';
import {defineStore} from 'pinia';
import {ref} from 'vue';
import {LoginInfo, OtpSendResponse, SocialProvider} from "@store/api/interfaces";
import {gtmForgetUserId} from "@composables/gtm";
import { UserOtpSendInterface, UserOtpVerifyInterface } from '@/interfaces/user-login.interface';
import { useRouter } from 'vue-router';

export const useAuthApi = defineStore('authApi', () => {
	const stored = localStorage.getItem('token');
	const token = ref<string|null>(
		stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '' ? stored : null
	);
	const router = useRouter();

	const http = {
		getToken: async (initRaw: string): Promise<LoginInfo> => {
			return await useApi<LoginInfo>('/login', {
				method: "POST",
				headers: {'X-Telegram-Signature': initRaw}
			});
		},
		getTokenBySocialProvider: async (provider: SocialProvider, response: string): Promise<LoginInfo> => {
			return await useApi<LoginInfo>(`/auth/${provider}${response}`)
		},
		sendOtp: async (data: UserOtpSendInterface): Promise<OtpSendResponse> => {
			return await useApi<OtpSendResponse>('/auth/email/otp', {
				method: "POST",
				body: JSON.stringify(data)
			});
		},
		verifyOtp: async (data: UserOtpVerifyInterface): Promise<LoginInfo> => {
			return await useApi<LoginInfo>('/auth/email/otp/verify', {
				method: "POST",
				body: JSON.stringify(data)
			});
		},
		// Cross-domain handoff: exchange a one-time code issued by the marketing
		// landing (step-vpn.com) for a real session on this domain. The raw JWT
		// never touches the landing — only this opaque, single-use code does.
		exchangeHandoff: async (handoffCode: string): Promise<LoginInfo> => {
			return await useApi<LoginInfo>('/auth/handoff/exchange', {
				method: "POST",
				body: JSON.stringify({ handoff_code: handoffCode })
			});
		},
	}

	const methods = {
		loginWithMiniApp: async (initRaw: string): Promise<LoginInfo> => {
			let responseAuth = await http.getToken(initRaw);
			methods.saveToken(responseAuth.token);
			return responseAuth;
		},
		loginWithSocialProvider: async (provider: SocialProvider, response: string): Promise<LoginInfo> => {
			let responseAuth = await http.getTokenBySocialProvider(provider, response);
			methods.saveToken(responseAuth.token);
			return responseAuth;
		},
		sendOtp: async (email: string): Promise<number> => {
			const response = await http.sendOtp({ email });
			return response.retry_after;
		},
		loginWithOtp: async (email: string, code: string, lang?: string): Promise<LoginInfo> => {
			const responseAuth = await http.verifyOtp({ email, code, lang });
			methods.saveToken(responseAuth.token);
			return responseAuth;
		},
		loginWithHandoff: async (handoffCode: string): Promise<LoginInfo> => {
			const responseAuth = await http.exchangeHandoff(handoffCode);
			methods.saveToken(responseAuth.token);
			return responseAuth;
		},
		saveToken: (responseToken: string) => {
			token.value = responseToken;
			localStorage.setItem('token', token.value);
		},
		getToken: (): string|null => {
			return token.value;
		},
		clearToken: () => {
			token.value = null;
			localStorage.removeItem('token');
			localStorage.removeItem('user_id');
			// The session is over: let the next sign-in announce its id to GTM
			// again, even if the same person comes back.
			gtmForgetUserId();
		},
		getAuthHeaders: (): HeadersInit => {
			const t = methods.getToken();
			if (!t) {
				throw new ApiAuthError({
					success: false,
					data: {},
					message: 'Unauthenticated',
					code: 'unauthorized',
				});
			}
			return {'Authorization': 'Bearer ' + t};
		},
		logout: () => {
			methods.clearToken();
			Sentry.setUser(null);
			router.push({ name: 'Login' });
		}
	};

	return {
		token,
		http,
		methods
	};
})
